import {
  type AccessTarget,
  type BudgetView,
  type ContractSummary,
  can,
  type Milestone,
  OBJECT_STATES,
  type ObjectState,
  PROJECT_STAGES,
  type ProjectDetail,
  type ProjectStage,
  seesMoney,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { CumulativeChart } from '../../components/charts';
import { HistoryList } from '../../components/History';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { useCostArticles } from '../../lib/articles';
import { date, tenge, tengeShort, todayLocal } from '../../lib/format';
import {
  budgetQuery,
  cashflowQuery,
  mln,
  paymentsQuery,
  projectQuery,
  projectTitle,
  STAGE_TONE,
} from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Page, Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { ContractsTable } from '../contracts/ContractsPage';
import { ProjectDocumentsPanel } from '../documents/DocumentsPage';
import { RelatedArticles } from '../kb/RelatedArticles';
import { NewRequestDialog, RequestsBoard } from '../procurement/ProcurementPage';
import { QualityPanel } from '../quality/QualityPanel';
import { ScheduleTab } from '../schedule/SchedulePage';
import { SiteDayPanel } from '../site/SiteDayPanel';
import { BudgetTab } from './BudgetTab';
import { EstimatesTab } from './EstimatesTab';
import { FinPlanTab } from './FinPlanTab';
import { ProjectAttentionPanel, ProjectDigests } from './ProjectAttention';
import { ProjectCardTab } from './ProjectCardTab';
import { WarehousePanel } from './WarehousePanel';

function ProjectProcurement({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  const me = useMe();
  return (
    <div className="flex flex-col gap-4">
      {can(me.access, 'procurement.request.create') ||
      Object.values(me.access.projects).some((x) => x.includes('procurement.request.create')) ? (
        <div>
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setOpen(true)}>
            Новая заявка
          </Button>
        </div>
      ) : null}
      <RequestsBoard projectId={projectId} />
      <NewRequestDialog open={open} onOpenChange={setOpen} projectId={projectId} />
    </div>
  );
}

const TABS = [
  { key: 'overview', title: 'Обзор' },
  { key: 'card', title: 'Карточка проекта' },
  { key: 'structure', title: 'Структура' },
  { key: 'psd', title: 'ПСД' },
  { key: 'estimates', title: 'Сметы' },
  { key: 'budget', title: 'Бюджет и маржа' },
  { key: 'schedule', title: 'График' },
  { key: 'resources', title: 'Ресурсы' },
  { key: 'procurement', title: 'Закупки' },
  { key: 'contracts', title: 'Договоры' },
  { key: 'site', title: 'Площадка' },
  { key: 'finance', title: 'Финансы' },
  { key: 'documents', title: 'Документы', module: 'documents' },
  { key: 'history', title: 'История' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/** Вкладки разделов других потоков — только тем, у кого есть право видеть раздел (и раздел входит в тариф) */
const TAB_PERMISSION: Partial<Record<TabKey, string>> = {
  psd: 'psd.view',
  estimates: 'estimate.view',
  schedule: 'schedule.view',
  resources: 'resources.view',
};

function useTarget(p: ProjectDetail | undefined) {
  return p
    ? {
        legalEntityId: p.legalEntityId,
        directionId: p.directionId,
        projectId: p.id,
        projectPath: [p.parentId],
      }
    : {};
}

function milestoneStatus(m: Milestone): { tone: 'success' | 'warning' | 'neutral' | 'danger'; text: string } {
  if (m.actualDate) return { tone: 'success', text: 'выполнено' };
  if (m.deviationDays && m.deviationDays > 0) return { tone: 'warning', text: `+${m.deviationDays} дн` };
  const t = todayLocal();
  if (m.baselineDate < t) return { tone: 'danger', text: 'просрочено' };
  return { tone: 'neutral', text: 'в графике' };
}

/* ───────────────────────── Обзор ───────────────────────── */

function MilestoneDialog({
  project,
  milestone,
  open,
  onOpenChange,
}: {
  project: ProjectDetail;
  milestone: Milestone | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => ({
    name: milestone?.name ?? '',
    kind: milestone?.kind ?? 'internal',
    baselineDate: milestone?.baselineDate ?? '',
    forecastDate: milestone?.forecastDate ?? '',
    actualDate: milestone?.actualDate ?? '',
    note: milestone?.note ?? '',
  }));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        kind: form.kind,
        baselineDate: form.baselineDate,
        forecastDate: form.forecastDate || null,
        actualDate: form.actualDate || null,
        note: form.note || null,
      };
      return milestone
        ? api<ProjectDetail>(`/milestones/${milestone.id}`, { method: 'PATCH', body })
        : api<ProjectDetail>(`/projects/${project.id}/milestones`, { body });
    },
    onSuccess: (p) => {
      qc.setQueryData(projectQuery(project.id).queryKey, p);
      qc.invalidateQueries({ queryKey: ['portfolio'] });
      toast(milestone ? 'Веха сохранена' : 'Веха добавлена');
      onOpenChange(false);
    },
  });
  const remove = useMutation({
    mutationFn: () => api<ProjectDetail>(`/milestones/${milestone!.id}`, { method: 'DELETE' }),
    onSuccess: (p) => {
      qc.setQueryData(projectQuery(project.id).queryKey, p);
      toast('Веха удалена');
      onOpenChange(false);
    },
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={milestone ? 'Веха проекта' : 'Новая веха'}
      footer={
        <>
          {milestone ? (
            <Button
              variant="danger"
              className="mr-auto"
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Удалить
            </Button>
          ) : null}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!form.name || !form.baselineDate}
            onClick={() => save.mutate()}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Название" className="sm:col-span-2">
          <Input value={form.name} onChange={set('name')} />
        </Field>
        <Field label="Вид">
          <Select value={form.kind} onChange={set('kind')}>
            <option value="internal">Внутренняя</option>
            <option value="contract">Договорная</option>
          </Select>
        </Field>
        <Field label="План (базовый)">
          <Input type="date" value={form.baselineDate} onChange={set('baselineDate')} />
        </Field>
        <Field label="Прогноз" hint="Если срок сдвигается">
          <Input type="date" value={form.forecastDate} onChange={set('forecastDate')} />
        </Field>
        <Field label="Факт">
          <Input type="date" value={form.actualDate} onChange={set('actualDate')} />
        </Field>
        <Field label="Примечание" className="sm:col-span-2">
          <Textarea rows={2} value={form.note} onChange={set('note')} />
        </Field>
      </div>
      <ErrorBox error={save.error ?? remove.error} className="mt-4" />
    </Dialog>
  );
}

function Milestones({ project, editable }: { project: ProjectDetail; editable: boolean }) {
  const [edit, setEdit] = useState<Milestone | null | 'new'>(null);
  return (
    <Panel
      title="Вехи"
      actions={
        editable ? (
          <Button
            size="sm"
            variant="secondary"
            icon={<Plus className="size-4" />}
            onClick={() => setEdit('new')}
          >
            Веха
          </Button>
        ) : null
      }
    >
      {project.milestones.length ? (
        <ul className="divide-y divide-line">
          {project.milestones.map((m) => {
            const st = milestoneStatus(m);
            return (
              <li key={m.id} className="flex items-center gap-4 py-2.5">
                <span className="num w-24 shrink-0 text-[14px] text-ink-2">
                  {date(m.forecastDate ?? m.baselineDate)}
                </span>
                <span className="min-w-0 flex-1 text-[14px] text-ink">
                  {m.name}
                  {m.kind === 'contract' ? (
                    <span className="ml-2 text-[12px] text-ink-3">договорная</span>
                  ) : null}
                </span>
                <Badge tone={st.tone}>{st.text}</Badge>
                {editable ? (
                  <IconButton label="Изменить веху" onClick={() => setEdit(m)}>
                    <Pencil className="size-4" />
                  </IconButton>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-[14px] text-ink-2">
          Вех нет. Они приходят из бюджета-графика или вносятся вручную.
        </p>
      )}
      {edit ? (
        <MilestoneDialog
          key={edit === 'new' ? 'new' : edit.id}
          project={project}
          milestone={edit === 'new' ? null : edit}
          open
          onOpenChange={(v) => !v && setEdit(null)}
        />
      ) : null}
    </Panel>
  );
}

export function BudgetSummary({ budget, projectId }: { budget: BudgetView; projectId: string }) {
  const sections = budget.lines.filter((l) => l.kind === 'section');
  const items = budget.lines.filter((l) => l.kind === 'item');
  const sumOf = (id: string, f: 'materials' | 'works') => {
    const own = budget.lines.find((l) => l.id === id)?.[f];
    if (own !== null && own !== undefined) return own;
    const list = items
      .filter((i) => i.parentId === id)
      .map((i) => i[f])
      .filter((v): v is number => v !== null);
    return list.length ? list.reduce((s, v) => s + v, 0) : null;
  };
  const undistributed = sections.filter((s) => !s.isDistributed && s.effectiveAmount > 0.5);
  const total = sections.reduce((s, l) => s + l.effectiveAmount, 0);
  const dist = sections.reduce((s, l) => s + l.distributed, 0);
  const altOf = (id: string) =>
    budget.lines.find((l) => l.parentId === id && l.kind === 'alternative' && l.isSelected);
  return (
    <>
      <ScrollX>
        <table className="w-full min-w-[640px] text-[14px]">
          <thead>
            <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
              <th className="py-2.5 pr-3 pl-5 font-medium">№</th>
              <th className="py-2.5 pr-3 font-medium">Раздел бюджета</th>
              <th className="py-2.5 pr-3 font-medium">ЛС</th>
              <th className="py-2.5 pr-3 text-right font-medium">ТМЦ</th>
              <th className="py-2.5 pr-3 text-right font-medium">СМР</th>
              <th className="py-2.5 pr-3 text-right font-medium">Итого</th>
              <th className="py-2.5 pr-5 font-medium">По неделям</th>
            </tr>
          </thead>
          <tbody>
            {sections.map((s) => {
              const alt = altOf(s.id);
              return (
                <tr key={s.id} className="border-t border-line align-top">
                  <td className="num py-2.5 pr-3 pl-5 text-ink-2">{s.code}</td>
                  <td className="py-2.5 pr-3">
                    <Link
                      to={`/projects/${projectId}/budget`}
                      search={{ line: s.id }}
                      className="inline-flex min-h-11 items-center text-ink hover:underline md:min-h-0"
                    >
                      {s.sectionCode ? `${s.sectionCode}, ` : ''}
                      {s.name}
                    </Link>
                    {alt ? <div className="text-[12px] text-ink-2">вариант: {alt.name}</div> : null}
                  </td>
                  <td className="num py-2.5 pr-3 whitespace-nowrap text-ink-2">{s.estimateNo ?? '—'}</td>
                  <td className="num py-2.5 pr-3 text-right">{alt ? '—' : mln(sumOf(s.id, 'materials'))}</td>
                  <td className="num py-2.5 pr-3 text-right">{alt ? '—' : mln(sumOf(s.id, 'works'))}</td>
                  <td className="num py-2.5 pr-3 text-right font-medium">{mln(s.effectiveAmount)}</td>
                  <td className="py-2.5 pr-5">
                    {s.effectiveAmount < 0.5 ? (
                      <span className="text-ink-3">—</span>
                    ) : s.isDistributed ? (
                      <Badge tone="success">да</Badge>
                    ) : (
                      <Badge tone="danger">{s.distributed > 0 ? 'частично' : 'нет'}</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="border-t border-line bg-surface-2 font-semibold">
              <td className="py-2.5 pr-3 pl-5" />
              <td className="py-2.5 pr-3">Итого</td>
              <td />
              <td />
              <td />
              <td className="num py-2.5 pr-3 text-right">{mln(total)}</td>
              <td className="num py-2.5 pr-5">{mln(dist)}</td>
            </tr>
          </tbody>
        </table>
      </ScrollX>
      {undistributed.length ? (
        <div className="m-5 rounded-md bg-danger-soft px-4 py-3 text-[14px] text-ink">
          {undistributed.length === 1 ? 'Раздел' : 'Разделы'} {undistributed.map((s) => s.code).join(', ')} на{' '}
          {tengeShort(undistributed.reduce((s, l) => s + l.effectiveAmount - l.distributed, 0))} не разложены
          по неделям. Портал не даст утвердить бюджет без графика оплат по каждому разделу.
        </div>
      ) : null}
    </>
  );
}

function Overview({ project }: { project: ProjectDetail }) {
  const me = useMe();
  const target = useTarget(project);
  // «Все деньги» — общий уровень или на этом проекте (ДС-18)
  const money = seesMoney(me.access, 'all', target);
  const editable = can(me.access, 'project.edit', target) || can(me.access, 'schedule.edit', target);
  const cash = useQuery({ ...cashflowQuery(project.id), enabled: money });
  const budget = useQuery({
    ...budgetQuery(project.id),
    enabled: money && can(me.access, 'budget.view', target),
  });
  const today = todayLocal();
  const planToDate = project.metrics.planToDate;
  const paid = project.metrics.paidToDate;

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <Panel
        title="Оплаты по бюджету: план и факт"
        meta={money ? `${tengeShort(paid)} из ${tengeShort(planToDate)} на сегодня` : 'скрыто'}
      >
        {!money ? (
          <p className="text-[14px] text-ink-2">
            Суммы скрыты: уровень доступа к деньгам назначает владелец компании.
          </p>
        ) : cash.isPending ? (
          <Spinner />
        ) : cash.isError ? (
          <ErrorBox error={cash.error} />
        ) : (
          <>
            <CumulativeChart points={cash.data} today={today} />
            <p className="mt-1 text-[12px] text-ink-2">
              млн ₸ нарастающим итогом; пунктир — план бюджета, линия — факт оплат
            </p>
          </>
        )}
      </Panel>
      <Milestones project={project} editable={editable} />
      <Panel
        title="Бюджет проекта, млн ₸"
        meta={
          budget.data?.current
            ? `${budget.data.current.name}, ${budget.data.current.status === 'approved' ? 'утверждён' : 'черновик'}`
            : undefined
        }
        bodyClassName="p-0"
      >
        {!money ? (
          <p className="p-5 text-[14px] text-ink-2">Суммы скрыты.</p>
        ) : budget.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : budget.isError ? (
          <div className="p-5">
            <ErrorBox error={budget.error} />
          </div>
        ) : budget.data.current ? (
          <BudgetSummary budget={budget.data} projectId={project.id} />
        ) : (
          <div className="p-5">
            <EmptyState
              title="Бюджета ещё нет"
              action={
                <Link
                  to={`/projects/${project.id}/budget`}
                  className="text-[14px] font-medium text-accent-text underline"
                >
                  Загрузить из Excel
                </Link>
              }
            >
              Загрузите бюджет-график (лист «График»): разделы, суммы и оплаты по неделям.
            </EmptyState>
          </div>
        )}
      </Panel>
      <div className="flex flex-col gap-6">
        {/* Находки всех разделов с кнопкой действия и сводки ПСД и ресурсов (ПФ-02, ПФ-03) */}
        {/* Блок — внутренний: внешним участникам (заказчик, подрядчик) не показывается */}
        {me.tenant?.staff ? (
          <>
            <ProjectAttentionPanel projectId={project.id} />
            <ProjectDigests projectId={project.id} />
          </>
        ) : null}
        {/* Склады проекта (СК-01, поток 5): список и кнопка «Склад объекта» */}
        <WarehousePanel
          project={project}
          target={{
            legalEntityId: project.legalEntityId,
            directionId: project.directionId,
            projectId: project.id,
            projectPath: [project.parentId],
          }}
        />
        {/* Качество и ОТ (поток 7): открытые замечания и приёмки по чек-листам */}
        <QualityPanel
          project={project}
          target={{
            legalEntityId: project.legalEntityId,
            directionId: project.directionId,
            projectId: project.id,
            projectPath: [project.parentId],
          }}
        />
        <SiteDayPanel
          project={project}
          target={{
            legalEntityId: project.legalEntityId,
            directionId: project.directionId,
            projectId: project.id,
            projectPath: [project.parentId],
          }}
        />
        {/* Статьи базы знаний к проекту (БЗ-08); нет статей — панели нет */}
        {me.tenant?.staff ? <RelatedArticles type="project" id={project.id} /> : null}
      </div>
    </div>
  );
}

/* ───────────────────────── Структура ───────────────────────── */

function ObjectDialog({
  project,
  object,
  onClose,
}: {
  project: ProjectDetail;
  object: ProjectDetail['objects'][number] | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    code: object?.code ?? '',
    name: object?.name ?? '',
    stage: (object?.stage ?? project.stage) as ProjectStage,
    state: (object?.state ?? 'on_track') as ObjectState,
    stateNote: object?.stateNote ?? '',
    estimateValue: object?.estimateValue?.toString() ?? '',
    parentId: object?.parentId ?? '',
  });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: f.code || null,
        name: f.name,
        stage: f.stage,
        state: f.state,
        stateNote: f.stateNote || null,
        estimateValue: f.estimateValue ? Number(f.estimateValue.replace(/\s/g, '').replace(',', '.')) : null,
        parentId: f.parentId || null,
      };
      return object
        ? api<ProjectDetail>(`/objects/${object.id}`, { method: 'PATCH', body })
        : api<ProjectDetail>(`/projects/${project.id}/objects`, { body });
    },
    onSuccess: (p) => {
      qc.setQueryData(projectQuery(project.id).queryKey, p);
      qc.invalidateQueries({ queryKey: ['portfolio'] });
      toast(object ? 'Объект сохранён' : 'Объект добавлен');
      onClose();
    },
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF({ ...f, [k]: e.target.value });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={object ? 'Объект' : 'Новый объект'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={f.name.trim().length < 2}
            onClick={() => save.mutate()}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Шифр" hint="Как в ПСД: ВНС-1, ВВ-1">
          <Input value={f.code} onChange={set('code')} />
        </Field>
        <Field label="Название">
          <Input value={f.name} onChange={set('name')} />
        </Field>
        <Field label="Входит в объект">
          <Select value={f.parentId} onChange={set('parentId')}>
            <option value="">— верхний уровень —</option>
            {project.objects
              .filter((o) => o.id !== object?.id)
              .map((o) => (
                <option key={o.id} value={o.id}>
                  {[o.code, o.name].filter(Boolean).join(' ')}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="Стадия">
          <Select value={f.stage} onChange={set('stage')}>
            {Object.entries(PROJECT_STAGES).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Состояние">
          <Select value={f.state} onChange={set('state')}>
            {Object.entries(OBJECT_STATES).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Сметная стоимость, ₸" hint="До подключения смет (этап 4) — вручную">
          <Input inputMode="decimal" value={f.estimateValue} onChange={set('estimateValue')} />
        </Field>
        <Field label="Комментарий к состоянию" className="sm:col-span-2">
          <Input value={f.stateNote} onChange={set('stateNote')} />
        </Field>
      </div>
      <ErrorBox error={save.error} className="mt-4" />
    </Dialog>
  );
}

function SubprojectDialog({ project, onClose }: { project: ProjectDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const [code, setCode] = useState(`${project.code}-${project.objects.length + 1}`);
  const [name, setName] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api<ProjectDetail>('/projects', {
        body: {
          legalEntityId: project.legalEntityId,
          parentId: project.id,
          siteName: project.siteName,
          code,
          name,
          stage: project.stage,
          contractNumber: project.contractNumber,
          customerId: project.customerId,
          region: project.region,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portfolio'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast('Подпроект создан');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Новый подпроект"
      description="У подпроекта свои бюджет, график, команда и доступы; свод поднимается в проект сам."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!code || name.trim().length < 2}
            onClick={() => save.mutate()}
          >
            Создать подпроект
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Код">
          <Input value={code} onChange={(e) => setCode(e.target.value)} />
        </Field>
        <Field label="Название">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
      </div>
      <ErrorBox error={save.error} className="mt-4" />
    </Dialog>
  );
}

function Structure({
  project,
  subprojects,
}: {
  project: ProjectDetail;
  subprojects: Array<{ id: string; code: string; name: string; stage: ProjectStage }>;
}) {
  const me = useMe();
  const qc = useQueryClient();
  const target = useTarget(project);
  const editable = can(me.access, 'project.edit', target);
  const [edit, setEdit] = useState<ProjectDetail['objects'][number] | null | 'new'>(null);
  const [sub, setSub] = useState(false);
  const remove = useMutation({
    mutationFn: (id: string) => api<ProjectDetail>(`/objects/${id}`, { method: 'DELETE' }),
    onSuccess: (p) => {
      qc.setQueryData(projectQuery(project.id).queryKey, p);
      toast('Объект удалён');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const money = seesMoney(me.access, 'all', target);
  return (
    <div className="grid gap-6">
      {!project.parentId ? (
        <Panel
          title="Подпроекты"
          actions={
            editable ? (
              <Button
                size="sm"
                variant="secondary"
                icon={<Plus className="size-4" />}
                onClick={() => setSub(true)}
              >
                Подпроект
              </Button>
            ) : null
          }
        >
          {subprojects.length ? (
            <ul className="divide-y divide-line">
              {subprojects.map((s) => (
                <li key={s.id} className="flex items-center gap-4 py-2.5">
                  <Link
                    to={`/projects/${s.id}`}
                    className="flex-1 text-[14px] font-medium text-ink hover:underline"
                  >
                    {s.name}
                  </Link>
                  <span className="num text-[13px] text-ink-2">{s.code}</span>
                  <Badge tone={STAGE_TONE[s.stage]}>{PROJECT_STAGES[s.stage]}</Badge>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[14px] text-ink-2">
              Подпроектов нет. Разделите проект, если у частей разные бюджеты, команды или доступы.
            </p>
          )}
        </Panel>
      ) : null}
      <Panel
        title="Объекты и сооружения"
        meta="разделы бюджета привязываются по шифру ПСД"
        bodyClassName="p-0"
        actions={
          editable ? (
            <Button
              size="sm"
              variant="secondary"
              icon={<Plus className="size-4" />}
              onClick={() => setEdit('new')}
            >
              Объект
            </Button>
          ) : null
        }
      >
        {project.objects.length ? (
          <ScrollX>
            <table className="w-full min-w-[720px] text-[14px]">
              <thead>
                <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                  <th className="py-2.5 pr-3 pl-5 font-medium">Шифр</th>
                  <th className="py-2.5 pr-3 font-medium">Объект</th>
                  <th className="py-2.5 pr-3 font-medium">Стадия</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Смета, млн ₸</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Бюджет, млн ₸</th>
                  <th className="py-2.5 pr-3 font-medium">Состояние</th>
                  <th className="py-2.5 pr-5" />
                </tr>
              </thead>
              <tbody>
                {project.objects.map((o) => (
                  <tr key={o.id} className="border-t border-line">
                    <td className={clsx('num py-2.5 pr-3', o.parentId ? 'pl-9' : 'pl-5')}>{o.code ?? '—'}</td>
                    <td className="py-2.5 pr-3">{o.name}</td>
                    <td className="py-2.5 pr-3">
                      <Badge tone={STAGE_TONE[o.stage]}>{PROJECT_STAGES[o.stage]}</Badge>
                    </td>
                    <td className="num py-2.5 pr-3 text-right">
                      {money ? mln(o.metrics.estimateValue) : '—'}
                    </td>
                    <td className="num py-2.5 pr-3 text-right">{money ? mln(o.metrics.budgetTotal) : '—'}</td>
                    <td className="py-2.5 pr-3">
                      <Badge
                        tone={
                          o.state === 'issues'
                            ? 'danger'
                            : o.state === 'risk'
                              ? 'warning'
                              : o.state === 'on_track'
                                ? 'success'
                                : 'neutral'
                        }
                      >
                        {OBJECT_STATES[o.state]}
                      </Badge>
                      {o.stateNote ? <div className="text-[12px] text-ink-2">{o.stateNote}</div> : null}
                    </td>
                    <td className="py-2.5 pr-5 text-right whitespace-nowrap">
                      {editable ? (
                        <>
                          <IconButton label="Изменить объект" onClick={() => setEdit(o)}>
                            <Pencil className="size-4" />
                          </IconButton>
                          <IconButton label="Удалить объект" onClick={() => remove.mutate(o.id)}>
                            <Trash2 className="size-4" />
                          </IconButton>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        ) : (
          <p className="p-5 text-[14px] text-ink-2">
            Объектов нет{project.parentId ? '' : ': они могут быть в подпроектах'}.
          </p>
        )}
      </Panel>
      {edit ? (
        <ObjectDialog project={project} object={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />
      ) : null}
      {sub ? <SubprojectDialog project={project} onClose={() => setSub(false)} /> : null}
    </div>
  );
}

/* ───────────────────────── Бюджет и маржа: бюджет-график и финансовый план ───────────────────────── */

const BUDGET_VIEWS = [
  { key: 'budget', title: 'Бюджет-график оплат' },
  { key: 'finplan', title: 'Финансовый план' },
] as const;

function BudgetArea({ project, initial }: { project: ProjectDetail; initial?: 'finplan' }) {
  const [view, setView] = useState<'budget' | 'finplan'>(() => {
    // Ссылка из «Ждёт меня» на шаг маршрута ФП открывает сразу финансовый план
    if (initial) return initial;
    try {
      return localStorage.getItem('epc:budget-view') === 'finplan' ? 'finplan' : 'budget';
    } catch {
      return 'budget';
    }
  });
  const pick = (v: 'budget' | 'finplan') => {
    setView(v);
    try {
      localStorage.setItem('epc:budget-view', v);
    } catch {
      /* без сохранения выбора */
    }
  };
  return (
    <div className="flex flex-col gap-4">
      <div
        role="tablist"
        aria-label="Бюджет и маржа"
        className="inline-flex w-fit rounded-md border border-line bg-surface p-0.5"
      >
        {BUDGET_VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            role="tab"
            aria-selected={view === v.key}
            onClick={() => pick(v.key)}
            className={clsx(
              'h-11 rounded-[5px] px-3 text-[13px] font-medium md:h-8',
              view === v.key ? 'bg-ink text-on-ink' : 'text-ink-2 hover:text-ink',
            )}
          >
            {v.title}
          </button>
        ))}
      </div>
      {view === 'budget' ? <BudgetTab project={project} /> : <FinPlanTab project={project} />}
    </div>
  );
}

/* ───────────────────────── Финансы: оплаты ───────────────────────── */

function Finance({ project }: { project: ProjectDetail }) {
  const costArticles = useCostArticles();
  const me = useMe();
  const qc = useQueryClient();
  const target = useTarget(project);
  const money = seesMoney(me.access, 'all', target);
  const canEdit =
    can(me.access, 'finance.edit', target) || can(me.access, 'procurement.payment.confirm', target);
  const q = useQuery({ ...paymentsQuery(project.id), enabled: money });
  // Оплата без заявки по договору закрывает его график платежей (ФН-02)
  const contracts = useQuery({
    queryKey: ['contracts', 'project', project.id, 'expense'],
    queryFn: () =>
      api<ContractSummary[]>('/contracts', { query: { projectId: project.id, direction: 'expense' } }),
    enabled: money && canEdit && can(me.access, 'contracts.view', target),
  });
  const payable = (contracts.data ?? []).filter((c) => ['signed', 'completed'].includes(c.status));
  const [f, setF] = useState({
    paidOn: todayLocal(),
    amount: '',
    direction: 'out' as 'in' | 'out',
    costArticle: '',
    document: '',
    objectId: '',
    note: '',
    contractId: '',
  });
  const add = useMutation({
    mutationFn: () =>
      api(`/projects/${project.id}/payments`, {
        body: {
          paidOn: f.paidOn,
          amount: Number(f.amount.replace(/\s/g, '').replace(',', '.')),
          direction: f.direction,
          costArticle: f.direction === 'out' ? f.costArticle || null : null,
          document: f.document || null,
          objectId: f.objectId || null,
          note: f.note || null,
          contractId: f.direction === 'out' ? f.contractId || null : null,
        },
      }),
    onSuccess: () => {
      toast('Оплата внесена');
      setF({ ...f, amount: '', document: '', note: '' });
      for (const k of ['payments', 'cashflow', 'project', 'portfolio', 'finplan'])
        qc.invalidateQueries({ queryKey: [k] });
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/payments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Оплата удалена');
      for (const k of ['payments', 'cashflow', 'project', 'portfolio'])
        qc.invalidateQueries({ queryKey: [k] });
    },
  });
  // Платёжный календарь виден с уровнем «Акты и счета» — ссылка на него и без уровня «Все деньги»
  const calendarLink = seesMoney(me.access, 'acts_invoices') ? (
    <div className="flex flex-col gap-1 rounded-lg border border-line bg-surface px-5 py-3 text-[14px] sm:flex-row sm:items-center sm:gap-4 xl:col-span-2">
      <span className="text-ink-2">
        Плановые платежи по договорам и заявки на оплату проекта по дням и неделям (ФН-02)
      </span>
      <Link
        to="/finance"
        search={{ projectId: project.id }}
        className="inline-flex min-h-11 items-center font-medium text-accent-text underline-offset-4 hover:underline sm:ml-auto md:min-h-0"
      >
        Платёжный календарь проекта
      </Link>
    </div>
  ) : null;
  if (!money)
    return (
      <div className="flex flex-col gap-6">
        {calendarLink}
        <EmptyState title="Фактические оплаты — с уровнем «Все деньги»">
          Уровень доступа к деньгам назначает владелец компании.
        </EmptyState>
      </div>
    );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate();
  };
  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      {calendarLink}
      <Panel
        title="Фактические оплаты"
        meta={
          q.data
            ? `оплачено ${tenge(q.data.filter((p) => p.direction === 'out').reduce((s, p) => s + p.amount, 0))}, поступило ${tenge(q.data.filter((p) => p.direction === 'in').reduce((s, p) => s + p.amount, 0))}`
            : undefined
        }
        bodyClassName="p-0"
      >
        {q.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : q.isError ? (
          <div className="p-5">
            <ErrorBox error={q.error} />
          </div>
        ) : q.data.length ? (
          <ScrollX>
            <table className="w-full text-[14px]">
              <thead>
                <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                  <th className="py-2.5 pr-3 pl-5 font-medium">Дата</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Сумма</th>
                  <th className="py-2.5 pr-3 font-medium">Вид, статья</th>
                  <th className="py-2.5 pr-3 font-medium">Документ</th>
                  <th className="py-2.5 pr-3 font-medium">Источник</th>
                  <th className="py-2.5 pr-5" />
                </tr>
              </thead>
              <tbody>
                {q.data.map((p) => (
                  <tr key={p.id} className="border-t border-line">
                    <td className="num py-2.5 pr-3 pl-5">{date(p.paidOn)}</td>
                    <td
                      className={clsx('num py-2.5 pr-3 text-right', p.direction === 'in' && 'text-success')}
                    >
                      {p.direction === 'in' ? '+' : ''}
                      {tenge(p.amount)}
                    </td>
                    <td className="py-2.5 pr-3">
                      {p.direction === 'in'
                        ? 'Поступление от заказчика'
                        : (p.costArticle ?? 'Оплата, статья не указана')}
                      {p.contractNumber ? (
                        <span className="block text-[12px] text-ink-2">по договору {p.contractNumber}</span>
                      ) : null}
                    </td>
                    <td className="py-2.5 pr-3">{p.document ?? '—'}</td>
                    <td className="py-2.5 pr-3 text-ink-2">{p.source === 'manual' ? 'вручную' : p.source}</td>
                    <td className="py-2.5 pr-5 text-right">
                      {canEdit ? (
                        <IconButton label="Удалить оплату" onClick={() => del.mutate(p.id)}>
                          <Trash2 className="size-4" />
                        </IconButton>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        ) : (
          <p className="p-5 text-[14px] text-ink-2">
            Оплат пока нет. Их вносит финансовый отдел; позже факт будет приходить из заявок на оплату и 1С.
          </p>
        )}
      </Panel>
      {canEdit ? (
        <Panel title="Внести оплату">
          <form onSubmit={submit} className="flex flex-col gap-4">
            <Field label="Вид">
              <Select
                value={f.direction}
                onChange={(e) => setF({ ...f, direction: e.target.value as 'in' | 'out' })}
              >
                <option value="out">Оплата поставщику или расход</option>
                <option value="in">Поступление от заказчика</option>
              </Select>
            </Field>
            {f.direction === 'out' && payable.length ? (
              <Field
                label="Договор"
                hint="Оплата по договору без заявки на оплату закрывает его график платежей; статья — из договора"
              >
                <Select value={f.contractId} onChange={(e) => setF({ ...f, contractId: e.target.value })}>
                  <option value="">— без договора —</option>
                  {payable.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.number} · {c.counterpartyName}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            {f.direction === 'out' ? (
              <Field
                label={f.contractId ? 'Статья расходов, если в договоре её нет' : 'Статья расходов'}
                hint={
                  f.contractId
                    ? 'По договору статья берётся из договора'
                    : 'Нужна для факта в финансовом плане'
                }
              >
                <Select value={f.costArticle} onChange={(e) => setF({ ...f, costArticle: e.target.value })}>
                  <option value="">— не указана —</option>
                  {costArticles.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            <Field label="Дата оплаты">
              <Input type="date" value={f.paidOn} onChange={(e) => setF({ ...f, paidOn: e.target.value })} />
            </Field>
            <Field label="Сумма, ₸">
              <Input
                inputMode="decimal"
                value={f.amount}
                onChange={(e) => setF({ ...f, amount: e.target.value })}
              />
            </Field>
            <Field label="Объект">
              <Select value={f.objectId} onChange={(e) => setF({ ...f, objectId: e.target.value })}>
                <option value="">— весь проект —</option>
                {project.objects.map((o) => (
                  <option key={o.id} value={o.id}>
                    {[o.code, o.name].filter(Boolean).join(' ')}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Платёжный документ">
              <Input value={f.document} onChange={(e) => setF({ ...f, document: e.target.value })} />
            </Field>
            <ErrorBox error={add.error} />
            <Button type="submit" variant="primary" loading={add.isPending} disabled={!f.amount}>
              Внести оплату
            </Button>
          </form>
        </Panel>
      ) : null}
    </div>
  );
}

/* ───────────────────────── Разделы других потоков в карточке ───────────────────────── */

/**
 * ПСД, надзор, изменения (поток 4) и ресурсы (поток 3) живут на своих страницах с отбором по проекту —
 * вкладка ведёт туда, чтобы не держать две копии одного экрана.
 */
function SectionLinks({
  project,
  title,
  links,
}: {
  project: ProjectDetail;
  title: string;
  links: Array<{ to: string; search: Record<string, string>; title: string; text: string }>;
}) {
  return (
    <Panel title={title} meta={`проект ${project.code}`}>
      <ul className="grid gap-3 md:grid-cols-2">
        {links.map((l) => (
          <li key={l.to + JSON.stringify(l.search)}>
            <Link
              to={l.to}
              search={l.search}
              className="flex min-h-11 flex-col gap-1 rounded-md border border-line px-4 py-3 hover:border-ink-3 hover:bg-surface-2"
            >
              <span className="text-[14px] font-medium text-ink underline-offset-4">{l.title} →</span>
              <span className="text-[13px] text-ink-2">{l.text}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function PsdLinks({ project }: { project: ProjectDetail }) {
  return (
    <div className="flex flex-col gap-6">
      <ProjectDigests projectId={project.id} only="psd" />
      <SectionLinks
        project={project}
        title="ПСД, надзор и изменения"
        links={[
          {
            to: '/psd',
            search: { project: project.id },
            title: 'Комплекты ПСД',
            text: 'Ожидаемые и полученные комплекты, ревизии, листы, передача в производство работ',
          },
          {
            to: '/supervision',
            search: { project: project.id },
            title: 'Авторский и технический надзор',
            text: 'Замечания, предписания, освидетельствование скрытых работ, вопросы проектировщику',
          },
          {
            to: '/changes',
            search: { project: project.id },
            title: 'Запросы на изменение',
            text: 'Изменения по проекту: сумма, срок, затронутые договоры, смета и график',
          },
        ]}
      />
    </div>
  );
}

/** Площадка проекта: сводка дня и переходы в разделы площадки с отбором по проекту */
function SiteLinks({ project }: { project: ProjectDetail }) {
  const target = {
    legalEntityId: project.legalEntityId,
    directionId: project.directionId,
    projectId: project.id,
    projectPath: [project.parentId],
  };
  return (
    <div className="flex flex-col gap-6">
      <SiteDayPanel project={project} target={target} />
      <SectionLinks
        project={project}
        title="Площадка проекта"
        links={[
          {
            to: '/site',
            search: { project: project.id },
            title: 'Отчёты смены',
            text: 'План на день, отчёты прорабов, проверка и приёмка объёмов',
          },
          {
            to: '/site/review',
            search: { project: project.id },
            title: 'Проверка отчётов',
            text: 'Отчёты на проверке, возвраты и «Открыть снова» принятого отчёта',
          },
          {
            to: '/site/photos',
            search: { project: project.id },
            title: 'Фото площадки',
            text: 'Фото и видео к работам, листам ПСД и замечаниям',
          },
        ]}
      />
    </div>
  );
}

function ResourceLinks({ project }: { project: ProjectDetail }) {
  return (
    <div className="flex flex-col gap-6">
      <ProjectDigests projectId={project.id} only="resources" />
      <SectionLinks
        project={project}
        title="Ресурсы проекта"
        links={[
          {
            to: '/resources',
            search: { project: project.id },
            title: 'Люди, техника и вахты',
            text: 'Загрузка ресурсов по неделям, табель, заезды и назначения на работы графика',
          },
          {
            to: '/estimates/resources',
            search: { projectId: project.id },
            title: 'Ведомость ресурсов по сметам',
            text: 'Трудозатраты, машины и материалы по позициям смет проекта',
          },
        ]}
      />
    </div>
  );
}

function History({ project }: { project: ProjectDetail }) {
  const q = useQuery({
    queryKey: ['history', 'projects', project.id],
    queryFn: () =>
      api<import('@epc/contracts').HistoryEntry[]>('/history', {
        query: { table: 'projects', rowId: project.id },
      }),
  });
  return (
    <Panel title="История карточки проекта">
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : (
        <HistoryList entries={q.data} />
      )}
    </Panel>
  );
}

/* ───────────────────────── Карточка ───────────────────────── */

/** Вкладки проекта: на узком экране полоса прокручивается к открытой вкладке */
function ProjectTabsNav({
  projectId,
  tabs,
  tab,
}: {
  projectId: string;
  tabs: Array<{ key: string; title: string }>;
  tab: string;
}) {
  const nav = useRef<HTMLElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: прокрутка — при смене вкладки, активная ищется в разметке
  useEffect(() => {
    const el = nav.current;
    const active = el?.querySelector<HTMLElement>('[data-tab-active]');
    if (!el || !active) return;
    // Только по горизонтали: страница при этом не прокручивается
    el.scrollLeft = Math.max(0, active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2);
  }, [tab]);
  return (
    <nav ref={nav} className="mb-6 overflow-x-auto border-b border-line" aria-label="Разделы проекта">
      <ul className="flex min-w-max gap-1">
        {tabs.map((t) => (
          <li key={t.key}>
            <Link
              to={t.key === 'overview' ? `/projects/${projectId}` : `/projects/${projectId}/${t.key}`}
              // «Обзор» — адрес проекта, он же начало адресов вкладок: активна только сама вкладка
              activeOptions={{ exact: true, includeSearch: false }}
              data-tab-active={tab === t.key ? '' : undefined}
              className={clsx(
                'relative inline-flex h-11 items-center px-3 text-[14px] font-medium md:h-10',
                tab === t.key ? 'text-ink' : 'text-ink-2 hover:text-ink',
              )}
            >
              {t.title}
              {tab === t.key ? <span className="absolute inset-x-2 -bottom-px h-0.5 bg-ink" /> : null}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function ProjectPage() {
  const params = useParams({ strict: false }) as { projectId: string; tab?: string };
  const me = useMe();
  const staff = Boolean(me.tenant?.staff);
  const q = useQuery(projectQuery(params.projectId));
  const target = useTarget(q.data);
  // Карточка проекта — внутренний документ: внешним участникам вкладка не показывается;
  // разделы других потоков — по праву видеть раздел
  const tabs = TABS.filter((t) => {
    if (t.key === 'card' && !staff) return false;
    const perm = TAB_PERMISSION[t.key];
    return !perm || !q.data || can(me.access, perm, target as AccessTarget);
  });
  // «/finplan» — тот же раздел «Бюджет и маржа», открытый на финансовом плане
  const fpLink = params.tab === 'finplan';
  const tab = (fpLink ? 'budget' : (tabs.find((t) => t.key === params.tab)?.key ?? 'overview')) as TabKey;
  const subs = useQuery({
    queryKey: ['projects', 'all'],
    queryFn: () =>
      api<Array<{ id: string; code: string; name: string; stage: ProjectStage; parentId: string | null }>>(
        '/projects',
        { query: { status: 'all' } },
      ),
  });
  // «Где вы находитесь»: портфель → проект над подпроектом → проект → вкладка
  const up = q.data?.parentId ? subs.data?.find((x) => x.id === q.data?.parentId) : null;
  const tabTitle = fpLink
    ? 'Финансовый план'
    : tab !== 'overview'
      ? TABS.find((t) => t.key === tab)?.title
      : null;
  useCrumbs(
    q.data
      ? [
          ...(up ? [{ label: up.code, to: `/projects/${up.id}`, title: `${up.code} ${up.name}` }] : []),
          {
            label: q.data.code,
            to: tabTitle ? `/projects/${q.data.id}` : undefined,
            title: `${q.data.code} ${q.data.name}`,
          },
          ...(tabTitle ? [{ label: tabTitle }] : []),
        ]
      : null,
    q.error,
  );

  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const p = q.data;
  const parent = p.parentId ? subs.data?.find((x) => x.id === p.parentId) : null;
  const children = (subs.data ?? []).filter((x) => x.parentId === p.id);
  const meta = [
    p.directionName,
    parent ? `входит в проект ${parent.code}` : null,
    p.contractNumber ? `договор ${p.contractNumber}` : null,
    p.customerName,
    [p.address, p.region].filter(Boolean).join(', ') || null,
    p.startDate ? `старт ${date(p.startDate)}` : null,
    p.finishDate ? `завершение ${date(p.finishDate)}` : null,
  ].filter(Boolean);

  return (
    <Page>
      <header className="mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.01em] text-ink md:text-[26px]">
            {projectTitle(p)}
          </h1>
          <Badge tone={STAGE_TONE[p.stage]}>{PROJECT_STAGES[p.stage]}</Badge>
          {p.status !== 'active' ? <Badge>{p.status === 'draft' ? 'черновик' : 'в архиве'}</Badge> : null}
        </div>
        <p className="mt-1 text-[14px] text-ink-2">
          {p.code}
          {meta.length ? ` · ${meta.join(' · ')}` : ''}
        </p>
        {p.description ? <p className="mt-2 max-w-4xl text-[13px] text-ink-2">{p.description}</p> : null}
      </header>
      <ProjectTabsNav projectId={p.id} tabs={tabs} tab={tab} />
      {tab === 'overview' ? <Overview project={p} /> : null}
      {tab === 'card' ? <ProjectCardTab project={p} /> : null}
      {tab === 'structure' ? <Structure project={p} subprojects={children} /> : null}
      {tab === 'budget' ? <BudgetArea project={p} initial={fpLink ? 'finplan' : undefined} /> : null}
      {tab === 'finance' ? <Finance project={p} /> : null}
      {tab === 'procurement' ? <ProjectProcurement projectId={p.id} /> : null}
      {tab === 'contracts' ? <ContractsTable projectId={p.id} /> : null}
      {tab === 'history' ? <History project={p} /> : null}
      {/* Разделы других потоков: сметы (2), график и ресурсы (3), ПСД (4) */}
      {tab === 'estimates' ? <EstimatesTab project={p} /> : null}
      {tab === 'schedule' ? <ScheduleTab project={p} /> : null}
      {tab === 'psd' ? <PsdLinks project={p} /> : null}
      {tab === 'resources' ? <ResourceLinks project={p} /> : null}
      {tab === 'site' ? <SiteLinks project={p} /> : null}
      {tab === 'documents' ? <ProjectDocumentsPanel key={p.id} project={p} /> : null}
    </Page>
  );
}
